/**
 * Settings: the system, the data source, and the subscription.
 *
 * Devices and security are separate routes rather than sections here. Devices
 * because it is a licensing artifact and deserves its own address (docs/18 §5.3);
 * security because "where do I sign out everywhere" should be findable without
 * scrolling past a broker status table.
 *
 * `/settings` is also `BILLING_PORTAL_RETURN_URL` — Stripe sends a user back
 * here after they change their plan, so the subscription block refetches on
 * mount and shows what actually happened.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { DesktopSettings } from '../components/DesktopSettings.js';
import {
  api, ApiError, type Health, type SubscriptionResponse,
} from '../lib/api.js';
import { Link } from '../lib/router.js';
import { openOnboarding } from './Onboarding.js';
import { useSession } from '../lib/session.js';
import { buildLines, injectedBuild } from '../lib/buildFacts.js';
import { appVersion, platform } from '../lib/runtime.js';

export function SettingsScreen({
  health, dataReason,
}: {
  health: Health | null;
  dataReason: string;
}): JSX.Element {
  const { user, status, admin, logout } = useSession();

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Settings</h2>
        {status === 'authenticated' && (
          <button className="btn btn-quiet compact" onClick={() => void logout()}>
            Sign out
          </button>
        )}
      </div>

      <section className="settings-block">
        <h3 className="panel-h">ACCOUNT</h3>
        {user ? (
          <dl className="kv">
            <dt>Signed in as</dt>
            <dd className="mono">
              {user.email}
              {status === 'authenticated' && !user.emailVerified && <VerifyPrompt />}
            </dd>
            <dt>Name</dt>
            <dd className="mono">
              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
            </dd>
            <dt>Market data category</dt>
            <dd className="mono">
              {user.subscriberCategory === 'professional' ? 'Professional' : 'Non-professional'}
            </dd>
          </dl>
        ) : (
          <p className="panel-body">
            Not signed in. <Link to="/login">Sign in</Link> or{' '}
            <Link to="/signup">create an account</Link>.
          </p>
        )}
        {status === 'demo' && (
          <p className="field-hint">
            This is the seeded demo identity that mock mode provides so the product
            runs with no database. Create a real account to keep anything.
          </p>
        )}
        <div className="settings-links">
          <Link to="/settings/broker" className="btn btn-quiet compact">Broker</Link>
          <Link to="/settings/trading" className="btn btn-quiet compact">Trading</Link>
          <Link to="/settings/devices" className="btn btn-quiet compact">Devices</Link>
          <Link to="/settings/security" className="btn btn-quiet compact">Security</Link>
          {/*
            Re-opens the first-run questionnaire, resuming from whatever was
            already answered. A user who skipped it, or who changed firms, has
            somewhere to go other than setting six things by hand.
          */}
          <button type="button" className="btn btn-quiet compact" onClick={openOnboarding}>
            Re-run setup
          </button>
        </div>
      </section>

      {/* Drawn only for an operator, and only ever a matter of DRAWING: every
          endpoint behind these answers 404 to a non-admin regardless of what
          the client decides to render. Grouped rather than added to the row
          above, which was already five buttons wide and about to read as
          clutter. */}
      {admin && (
        <section className="settings-block">
          <h3 className="panel-h">ADMIN</h3>
          <p className="panel-body dim">
            Operator surfaces. None of these is visible to a signed-in trader —
            the API answers 404 rather than 403, so the endpoints do not confirm
            they exist.
          </p>
          <div className="settings-links">
            <Link to="/settings/errors" className="btn btn-quiet compact">Crash reports</Link>
            <Link to="/settings/news-status" className="btn btn-quiet compact">News status</Link>
            <Link to="/settings/ai-status" className="btn btn-quiet compact">AI status</Link>
            <Link to="/settings/ai-audit" className="btn btn-quiet compact">AI audit</Link>
            <Link to="/settings/entitlements" className="btn btn-quiet compact">Entitlements</Link>
          </div>
        </section>
      )}

      {/*
        What this surface is running. Operator-only, and only ever a matter of
        DRAWING — every value here is read from the shell or from a constant
        Vite baked into the bundle, so nothing is withheld by hiding it. Same
        contract as the mobile card in `apps/mobile/src/screens/SettingsScreen`.

        This one card serves the browser AND the desktop shell, because
        `tauri.conf.json` points `frontendDist` at `apps/web/dist`: the desktop
        app IS this bundle. What differs is what there is to say. The shell has
        a real version; a browser has only whatever was deployed last, so it
        reports the build time instead of inventing a number that would never
        change while the code under it did.
      */}
      {admin && (
        <section className="settings-block">
          <h3 className="panel-h">BUILD</h3>
          <div className="kv">
            {buildLines({
              platform: platform(),
              appVersion: appVersion(),
              ...injectedBuild(),
            }).map((line) => (
              <p key={line} className="panel-body mono">{line}</p>
            ))}
          </div>
        </section>
      )}

      <SubscriptionBlock />

      <section className="settings-block">
        <h3 className="panel-h">SYSTEM</h3>
        {health && (
          <dl className="kv">
            <dt>Mode</dt><dd className="mono">{health.mode}</dd>
            <dt>Storage</dt><dd className="mono">{health.repository}</dd>
            <dt>Broker adapter</dt><dd className="mono">{health.broker}</dd>
            <dt>Broker status</dt><dd className="mono">{health.brokerHealth.reason}</dd>
            <dt>AI model layer</dt>
            <dd className="mono">{health.aiModelLayer ? 'enabled' : 'Layer 1 only (no API key)'}</dd>
            <dt>Delayed feed</dt>
            <dd className="mono">{health.delayedFeed ? 'enabled' : 'disabled — Tier F only'}</dd>
            <dt>Email</dt>
            <dd className="mono">
              {health.email === 'console'
                ? 'not configured — resets are logged, not sent'
                : health.email}
            </dd>
            <dt>Instruments</dt><dd className="mono">{health.instruments}</dd>
          </dl>
        )}
      </section>

      <section className="settings-block">
        <h3 className="panel-h">DATA SOURCE</h3>
        <p className="panel-body">{dataReason || '—'}</p>
      </section>

      {/* Renders nothing in the browser. */}
      <DesktopSettings />
    </div>
  );
}

/**
 * Confirm-your-address, offered where the address is shown.
 *
 * Deliberately not a modal, not a blocking interstitial, and not a gate on
 * anything. An unconfirmed address costs the user nothing today; it costs them
 * the ability to recover the account later, which is what the copy says. Making
 * it louder than that would be the product nagging about its own bookkeeping.
 */
function VerifyPrompt(): JSX.Element {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [message, setMessage] = useState('');

  async function resend(): Promise<void> {
    setState('sending');
    try {
      const res = await api.resendVerification();
      setMessage(res.message);
      setState('sent');
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : 'Could not send just now.');
      setState('failed');
    }
  }

  return (
    <>
      <span className="chip chip-caution verify-chip">Unconfirmed</span>
      <span className="field-hint">
        {state === 'sent' || state === 'failed' ? message : (
          <>
            Confirm this address so you can reset your password if you ever lose it.
            Without it there is no way back into the account.{' '}
            <button className="compact linkish" onClick={() => void resend()} disabled={state === 'sending'}>
              {state === 'sending' ? 'Sending…' : 'Send the link'}
            </button>
          </>
        )}
      </span>
    </>
  );
}

function SubscriptionBlock(): JSX.Element {
  const { status } = useSession();
  const [sub, setSub] = useState<SubscriptionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.subscription()
      .then((s) => { if (!cancelled) setSub(s); })
      .catch(() => { /* 401 in anonymous mode is expected; the block just stays quiet. */ });
    return () => { cancelled = true; };
  }, [status]);

  async function openPortal(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const { url } = await api.portal();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open the billing portal.');
      setBusy(false);
    }
  }

  if (!sub) return <></>;

  const stripe = sub.subscriptions.find((s) => s.source === 'stripe');

  return (
    <section className="settings-block">
      <h3 className="panel-h">SUBSCRIPTION</h3>
      <dl className="kv">
        <dt>Plan</dt>
        <dd className="mono">{sub.features.name}</dd>
        {stripe && (
          <>
            <dt>Status</dt>
            <dd className="mono">{stripe.status}</dd>
            <dt>{stripe.cancelAtPeriodEnd ? 'Ends' : 'Renews'}</dt>
            <dd className="mono">
              {stripe.currentPeriodEnd
                ? new Date(stripe.currentPeriodEnd).toLocaleDateString()
                : '—'}
            </dd>
          </>
        )}
        <dt>Market-data devices</dt>
        <dd className="mono">{sub.features.maxDevices}</dd>
        <dt>Written analysis</dt>
        <dd className="mono">{sub.features.writtenAnalysis ? 'included' : 'Layer 1 only'}</dd>
      </dl>

      {error && <div className="form-error" role="alert">{error}</div>}

      <div className="settings-links">
        {stripe ? (
          <button className="btn btn-quiet compact" onClick={() => void openPortal()} disabled={busy}>
            {busy ? 'Opening…' : 'Manage billing'}
          </button>
        ) : (
          <Link to="/pricing" className="btn btn-primary compact">See plans</Link>
        )}
      </div>
    </section>
  );
}
